import { GitCompare, Share2, User } from 'lucide-react';
import { PriceDisplay } from '../PriceDisplay';
import PriceOrContact from '../PriceOrContact';
import StoreBadges from '../StoreBadges';
import PhoneNumberLockup from '../PhoneNumberLockup';
import { toast } from 'sonner';
import { getDisplayNumber } from '../../utils/digitMask';
import { useOperatorView } from '../../hooks/usePhoneOperators';
import OperatorLogo from '../OperatorLogo';

/**
 * NumberCard — premium phone-number card (homepage sliders + listing pages).
 * Variants: 'business' (default) | 'personal' | 'discounted' | 'marketplace'
 * One frame for all: operator strip → number lockup (grouped, masked digits in gold) → price → actions.
 */
export const NumberCard = ({
  item,
  isDarkMode,
  isAr,
  isInCompare,
  toggleCompare,
  onOpenModal,
  variant = 'business'
}) => {
  const op = useOperatorView(item);
  const shown = getDisplayNumber(item);
  const isDiscounted = variant === 'discounted';
  const isPersonal = variant === 'personal';
  const negotiable = item.negotiable === true || item.negotiable === 'true';

  const discountPercent = item.discount_percentage || item.discountPercentage || 0;
  const originalPrice = item.original_price || item.originalPrice || item.price;
  const salePrice = item.salePrice || item.price;

  const handleShare = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({ title: `${shown} - CloudGM`, text: `رقم مميز ${shown}`, url: window.location.origin + `/mobile-numbers?id=${item.id}` });
    } else {
      navigator.clipboard.writeText(`${shown} - ${item.price} AED`);
      toast.success(isAr ? 'تم النسخ' : 'Copied');
    }
  };

  const testId = isDiscounted ? `number-card-disc-${item.id}` : isPersonal ? `number-card-personal-${item.id}` : variant === 'marketplace' ? `number-card-mp-${item.id}` : `number-card-${item.id}`;
  const frame = isDarkMode
    ? 'bg-[#121520] border border-[#1E9BD9]/20 hover:border-[#1E9BD9]/55 hover:shadow-[0_12px_32px_rgba(30,155,217,0.18)]'
    : 'bg-white border border-[#1E9BD9]/20 hover:border-[#1E9BD9]/60 shadow-sm hover:shadow-[0_12px_32px_rgba(30,155,217,0.14)]';

  return (
    <div
      className={`group relative flex-shrink-0 w-40 sm:w-44 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${frame}`}
      data-testid={testId}
    >
      {/* brand accent line (operator colour → gold) */}
      <div className="h-[2px] w-full" style={{ background: `linear-gradient(90deg, ${op.accent}, #F5A962)` }} />

      {isInCompare(item.id) && (
        <div className="absolute top-3 end-2 w-5 h-5 rounded-full bg-[#1E9BD9] flex items-center justify-center z-10 animate-pulse">
          <GitCompare className="h-3 w-3 text-white" />
        </div>
      )}

      <div className="p-3 cursor-pointer" onClick={() => onOpenModal(isPersonal ? { ...item, seller_type: 'personal' } : item)}>
        {/* Operator row */}
        <div className="flex items-center justify-between mb-3" dir="ltr">
          <OperatorLogo src={op.logo} name={isAr ? op.nameAr : op.nameEn} accent={op.accent} className="w-8 h-8" letterClass="text-sm" testId={`number-card-operator-${item.id}`} />
          <div className="flex items-center gap-1">
            {isDiscounted && discountPercent > 0 && (
              <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                -{Math.round(discountPercent)}%
              </span>
            )}
            {!isDiscounted && negotiable && (
              <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                {isAr ? 'تفاوض' : 'Neg'}
              </span>
            )}
            {isPersonal && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F5A962]/15 text-[#F59E0B]" data-testid={`personal-badge-${item.id}`}>
                <User className="h-2.5 w-2.5" />
                {isAr ? 'شخصي' : 'Personal'}
              </span>
            )}
          </div>
        </div>

        {/* Number lockup */}
        <div className={`rounded-xl py-2.5 px-1 mb-2.5 flex items-center justify-center border ${isDarkMode ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'}`}>
          <PhoneNumberLockup value={shown} size="card" testId={`number-lockup-${item.id}`} />
        </div>

        {/* Price */}
        <div className="text-center h-10 flex flex-col justify-center items-center">
          {item.priceOnRequest ? (
            <PriceOrContact item={{ ...item, store: { whatsapp: item.store_whatsapp } }} variant="card" itemLabel={shown} />
          ) : isDiscounted || (item.hasDiscount && item.salePrice) ? (
            <>
              <span className="text-red-500 font-bold text-sm leading-tight"><PriceDisplay price={salePrice} /></span>
              <span className="text-gray-400 line-through text-xs leading-tight"><PriceDisplay price={originalPrice} /></span>
            </>
          ) : (
            <span className="font-bold text-sm" style={{ color: op.accent }}>
              <PriceDisplay price={item.price} />
            </span>
          )}
          {item.store_owner && !isDiscounted && (
            <div className="mt-1">
              <StoreBadges owner={item.store_owner} size="sm" align="center" />
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className={`grid grid-cols-2 border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
        <button
          onClick={(e) => { e.stopPropagation(); toggleCompare(item); }}
          className={`py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
            isInCompare(item.id)
              ? 'bg-[#1E9BD9] text-white'
              : isDarkMode ? 'text-gray-500 hover:bg-white/5 hover:text-[#1E9BD9]' : 'text-gray-400 hover:bg-gray-50 hover:text-[#1E9BD9]'
          }`}
          data-testid={`compare-btn-${item.id}`}
        >
          <GitCompare className="h-3 w-3" />
          {isAr ? 'قارن' : 'Compare'}
        </button>
        <button
          onClick={handleShare}
          className={`py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-1 border-s ${
            isDarkMode ? 'border-white/5 text-gray-500 hover:bg-white/5 hover:text-emerald-400' : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-emerald-600'
          }`}
          data-testid={`share-btn-${item.id}`}
        >
          <Share2 className="h-3 w-3" />
          {isAr ? 'شارك' : 'Share'}
        </button>
      </div>
    </div>
  );
};

export default NumberCard;
